import { appStorageKeyFromUrl } from "./storageContract";
import { isMultipleSpeakerName } from "./speakerPortraitCatalog";

/**
 * The stand-in artwork an event carries until somebody uploads its own.
 *
 * An event with no art rendered as an empty box, a broken-image glyph, or
 * whatever the surrounding background happened to be — so a list of events was
 * ragged and it was never clear whether art was missing or merely still loading.
 *
 * Drawn rather than shipped. A single stock file would repeat across every event
 * in a list, which reads as one duplicated event; a per-event upload is exactly
 * what does not exist yet. This is an inline SVG built from the event's own
 * identity, so it:
 *
 *   - cannot 404, and needs no storage, no upload, and no network round trip;
 *   - differs per event, so a list of placeholders still looks like a list;
 *   - is stable — the same event draws the same image every render, so it does
 *     not shimmer between reloads or differ between two screens.
 *
 * Deliberately unmistakable for real artwork: a flat brand-gradient composition
 * with no words, initials, faces, or headshots. Nobody should mistake it for a
 * speaker portrait while generated event-context artwork is still being prepared.
 */

/** The brand pairs a placeholder can be drawn in, chosen by the event's slug. */
const PALETTES = [
  { from: "#E91E8C", to: "#7C1D6F" },
  { from: "#00D4FF", to: "#1D4E89" },
  { from: "#A855F7", to: "#4C1D95" },
  { from: "#F5A623", to: "#92400E" },
  { from: "#22C55E", to: "#14532D" },
] as const;

/**
 * A small stable number from a string.
 *
 * FNV-1a: short, dependency-free, and — the property that matters here —
 * deterministic across processes, so the server and the browser pick the same
 * palette for the same event.
 */
function hashString(value: string) {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

function escapeXml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

/**
 * The placeholder for one event, as a data URI usable in any `src`.
 *
 * `width`/`height` set the SVG's coordinate space, not its rendered size — the
 * element it goes into still controls that. They exist so the aspect ratio can
 * match the slot: a wide hero and a square thumbnail want different framing of
 * the same drawing.
 */
export function eventPlaceholderArt({
  slug,
  title,
  width = 1200,
  height = 630,
}: {
  slug: string;
  title: string;
  width?: number;
  height?: number;
}) {
  const palette = PALETTES[hashString(slug || title || "event") % PALETTES.length];
  const angle = hashString(`${slug}-angle`) % 60;
  const orbit = 18 + (hashString(`${slug}-orbit`) % 28);
  const beam = 52 + (hashString(`${slug}-beam`) % 24);

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="${width}" height="${height}" preserveAspectRatio="xMidYMid slice" role="img" aria-label="${escapeXml(title)} event artwork">
  <defs>
    <linearGradient id="g" gradientTransform="rotate(${angle})">
      <stop offset="0%" stop-color="${palette.from}"/>
      <stop offset="100%" stop-color="${palette.to}"/>
    </linearGradient>
    <radialGradient id="r">
      <stop offset="0%" stop-color="#FFFFFF" stop-opacity=".46"/>
      <stop offset="100%" stop-color="#FFFFFF" stop-opacity="0"/>
    </radialGradient>
  </defs>
  <rect width="100" height="100" fill="#080C18"/>
  <rect width="100" height="100" fill="url(#g)" opacity=".72"/>
  <circle cx="${orbit}" cy="24" r="34" fill="url(#r)" opacity=".5"/>
  <circle cx="84" cy="78" r="27" fill="none" stroke="#FFFFFF" stroke-opacity=".2" stroke-width=".7"/>
  <circle cx="84" cy="78" r="17" fill="none" stroke="#FFFFFF" stroke-opacity=".13" stroke-width=".55"/>
  <path d="M-8 ${beam} C 18 ${beam - 28}, 38 ${beam + 24}, 108 ${beam - 12}" fill="none" stroke="#FFFFFF" stroke-opacity=".26" stroke-width="1.25"/>
  <path d="M-8 ${beam + 8} C 24 ${beam - 10}, 55 ${beam + 28}, 108 ${beam - 2}" fill="none" stroke="#FFFFFF" stroke-opacity=".12" stroke-width=".8"/>
</svg>`;

  // encodeURIComponent rather than base64: it keeps the markup readable in
  // devtools, and avoids pulling a base64 implementation into shared code that
  // both Node and the browser run.
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * The event's own artwork when it has some, and the placeholder when it does not.
 *
 * One call site for the decision, so no screen can accidentally render an empty
 * box by forgetting the fallback.
 */
export function eventArtOrPlaceholder(event: {
  slug: string;
  title: string;
  heroImage?: string | null;
  /** Explicit administrator artwork wins over generated and legacy artwork. */
  heroImageIsManual?: boolean;
  generatedImage?: string | null;
  /** Optional section-specific generic art, used before the drawn fallback. */
  fallbackImage?: string | null;
}, size?: { width?: number; height?: number }) {
  const uploaded = event.heroImage?.trim();
  if (uploaded && event.heroImageIsManual) return { url: uploaded, isPlaceholder: false as const };
  const generated = event.generatedImage?.trim();
  if (generated) return { url: generated, isPlaceholder: false as const };
  if (uploaded) return { url: uploaded, isPlaceholder: false as const };
  const fallbackImage = event.fallbackImage?.trim();
  if (fallbackImage) return { url: fallbackImage, isPlaceholder: true as const };
  return {
    url: eventPlaceholderArt({ slug: event.slug, title: event.title, ...size }),
    isPlaceholder: true as const,
  };
}

/**
 * The artwork an administrator chose for an event, or nothing.
 *
 * `heroImage` means two different things. On a catalog entry it is a legacy value
 * that is as often a speaker's headshot or a panel photograph as it is event art —
 * which is why the public surfaces stopped reading it. On a managed event it is the
 * Event Picture an administrator selected in the CRM, and that upload always lives
 * in application storage. The storage path is what tells them apart: a catalog
 * event migrated into the managed table keeps its legacy value on the legacy
 * /media path and still fails this check, while an administrator's upload passes.
 *
 * An allow-list, deliberately. Anything not recognisably an upload is treated as
 * legacy, so a new kind of stray value degrades to "no artwork", never to a face
 * in the wrong frame.
 */
export function administratorEventArtwork(heroImage: string | null | undefined): string | undefined {
  const url = heroImage?.trim();
  return url && appStorageKeyFromUrl(url) ? url : undefined;
}

/**
 * Uploaded art for one named speaker needs a human prominence check. Pixels do
 * not carry trustworthy identity labels, so the application never guesses which
 * person in a group image is the speaker. Panels are intentionally exempt.
 */
export function eventArtworkNeedsSpeakerReview(event: {
  speaker?: string | null;
  heroImage?: string | null;
}) {
  const speaker = event.speaker?.trim() ?? "";
  return Boolean(
    speaker
    && !isMultipleSpeakerName(speaker)
    && administratorEventArtwork(event.heroImage),
  );
}

/**
 * The featured image for an event, the same on every surface.
 *
 * The CRM said "Using uploaded Event Picture" while the public page showed the
 * gradient placeholder for the same event: the page read only the curated catalog
 * field, and threw the administrator's upload out with the legacy `heroImage`
 * values it was right to ignore. One resolver for every tile, hero and preview,
 * so the two cannot disagree again.
 *
 * Precedence: the administrator's upload, then the curated catalog artwork, then
 * generated artwork, then the drawn placeholder. The upload outranks the curated
 * file because it is the one thing an administrator can change without a deploy.
 */
export function eventFeaturedArtwork(event: {
  slug: string;
  title: string;
  /** Curated artwork shipped with a catalog entry. */
  eventImage?: string | null;
  /** The Event Picture chosen in the CRM, or a legacy catalog value. */
  heroImage?: string | null;
  generatedImage?: string | null;
}, size?: { width?: number; height?: number }) {
  const chosen = administratorEventArtwork(event.heroImage) ?? event.eventImage?.trim() ?? undefined;
  return eventArtOrPlaceholder({
    slug: event.slug,
    title: event.title,
    heroImage: chosen || undefined,
    heroImageIsManual: Boolean(chosen),
    generatedImage: chosen ? null : event.generatedImage,
  }, size);
}
